import type { Model, PriceConfidence, ModelStatus, Modality, ModelPricing } from "../types";

export function formatUsd(n: number | null | undefined, digits = 3): string {
  if (n == null || Number.isNaN(n)) return "—";
  if (n === 0) return "$0";
  if (n < 0.001) return `$${n.toFixed(6)}`;
  if (n < 0.01) return `$${n.toFixed(4)}`;
  if (n < 1) return `$${n.toFixed(Math.min(digits + 1, 4))}`;
  return `$${n.toFixed(2)}`;
}

export function unitLabel(unit: ModelPricing["normalizedUnit"] | string | undefined): string {
  if (unit === "per_image") return "/img";
  if (unit === "per_second") return "/sec";
  if (unit === "per_mp") return "/MP";
  return "";
}

export function formatNormalized(model: Model): string {
  const { normalizedUsd, normalizedUnit } = model.pricing;
  if (normalizedUsd == null || normalizedUnit === "unknown") return "—";
  return `${formatUsd(normalizedUsd)}${unitLabel(normalizedUnit)}`;
}

/** Sortable / highlightable USD: 1 image or 1 second (or 1 MP ≈ 1024²). */
export function comparableUsd(model: Model): number | null {
  const { normalizedUsd, normalizedUnit } = model.pricing;
  if (normalizedUsd == null) return null;
  if (
    normalizedUnit === "per_image" ||
    normalizedUnit === "per_second" ||
    normalizedUnit === "per_mp"
  ) {
    return normalizedUsd;
  }
  return null;
}

export function scenarioCost(model: Model, scenario: "1_image" | "5s_video"): number | null {
  const n = model.pricing.normalizedUsd;
  if (n == null) return null;
  const u = model.pricing.normalizedUnit;
  if (scenario === "1_image") {
    if (u === "per_image" || u === "per_mp") return n;
    return null;
  }
  if (u === "per_second") return n * 5;
  return null;
}

export function isHostAggregator(providerId: string): boolean {
  return [
    "fal",
    "replicate",
    "together",
    "fireworks",
    "deepinfra",
    "novita",
    "wavespeed",
    "atlascloud",
    "kie",
    "cloudflare",
  ].includes(providerId);
}

export function confidenceLabel(c: PriceConfidence): string {
  if (c === "verified") return "Verified";
  if (c === "estimated") return "Estimated";
  return "Unknown";
}

export function statusLabel(s: ModelStatus): string {
  if (s === "ga") return "GA";
  if (s === "preview") return "Preview";
  if (s === "deprecated") return "Deprecated";
  return "Beta";
}

export function modalityLabel(m: Modality): string {
  if (m === "image") return "Image";
  if (m === "video") return "Video";
  return "Both";
}

export function qualityRankLabel(q: import("../types").QualityRank | undefined): string {
  if (q === "sota") return "SOTA";
  if (q === "good") return "Good";
  if (q === "usable") return "Usable";
  return "—";
}
